import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} ProtectionResult
 * @property {number} price - The protection price in store currency units.
 * @property {string} variant_id - The protection variant ID.
 */

/**
 * @typedef {Object} CartItem
 * @property {string} [handle] - The product handle.
 * @property {string} [vendor] - The product vendor.
 * @property {number} price - The item price in cents.
 * @property {number} final_line_price - The line price in cents.
 * @property {number} variant_id - The variant ID.
 */

/**
 * @typedef {Object} Cart
 * @property {CartItem[]} items - Cart items.
 * @property {number} total_price - Cart total in cents.
 * @property {string} currency - The cart currency.
 */

const NAVIDIUM_WAIT_MS = 3000;
const ZERO_DECIMAL_CURRENCIES = ['BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'];

/**
 * Whether a cart line is the Navidium package protection line.
 * Matches the handles Navidium itself removes, not any product with "protection" in it.
 *
 * @param {CartItem} item
 * @returns {boolean}
 */
function isProtectionItem(item) {
  const handle = item.handle?.toLowerCase() ?? '';
  return item.vendor?.toLowerCase() === 'navidium' || handle.includes('shipping-protection') || handle.includes('navdium');
}

/**
 * Package protection in the cart drawer footer (Navidium).
 *
 * Shows the fee, adds it when "Protected checkout" is clicked and removes it for
 * "Checkout without package protection". When Navidium returns no fee, or is turned off for
 * the shopper's country in Navidium, it switches to a plain checkout button.
 *
 * The fee, the button label and the button total are data-morph-ignore: this component owns
 * their text, so a section re-render can't reset it, and updates them after each cart change.
 *
 * The /cart page renders only the fee row (snippets/cart-package-protection.liquid), without
 * the checkout refs; there the component just shows the fee and sets data-no-price.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [checkoutButton] - The checkout button.
 * @property {HTMLElement} [checkoutLabel] - The checkout button label.
 * @property {HTMLElement} [checkoutTotal] - The total shown in the checkout button.
 * @property {HTMLElement} [price] - The protection fee.
 * @property {HTMLAnchorElement} [withoutLink] - The checkout without protection link.
 * @property {HTMLElement} [error] - The error message container.
 * @property {HTMLElement} [errorMessage] - The error message text.
 * @property {HTMLTemplateElement} [moneyFormat] - The shop money format.
 *
 * @extends {Component<Refs>}
 */
class CartPackageProtectionComponent extends Component {
  /** @type {'loading' | 'ready' | 'unavailable' | 'off'} */
  #state = 'loading';
  #priceCents = 0;
  #protectionInCart = false;
  #checkoutInProgress = false;
  #refreshId = 0;
  #cartTotal = Number(this.dataset.cartTotal) || 0;
  /** @type {Promise<void>} */
  #pendingRefresh = Promise.resolve();

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.protectionState === 'off') {
      this.#state = 'off';
      return;
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartChange);
    document.addEventListener(ThemeEvents.cartOpen, this.#handleCartChange);
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartChange);
    document.removeEventListener(ThemeEvents.cartOpen, this.#handleCartChange);
  }

  #handleCartChange = () => {
    this.#refresh();
  };

  /** Re-reads the cart and recalculates the fee. Only the latest call renders. */
  #refresh() {
    if (this.#state === 'off') return;

    this.#pendingRefresh = this.#recalculate(++this.#refreshId);
  }

  /** @param {number} refreshId */
  async #recalculate(refreshId) {
    try {
      const cart = await this.#fetchCart();
      const protectionItem = cart.items.find(isProtectionItem);
      const priceCents = protectionItem ? protectionItem.final_line_price : await this.#calculatePrice(cart);

      if (refreshId !== this.#refreshId) return;

      this.#protectionInCart = Boolean(protectionItem);
      this.#priceCents = priceCents;
      this.#cartTotal = cart.total_price;
      this.#state = priceCents > 0 ? 'ready' : 'unavailable';
    } catch (error) {
      if (refreshId !== this.#refreshId) return;
      console.error('Package protection: could not calculate the fee', error);
      this.#state = 'unavailable';
    }

    this.#render();
  }

  /** @returns {Promise<Cart>} */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  }

  /**
   * The protection fee for a cart without protection, in cents of the cart currency, or 0
   * when protection isn't offered.
   *
   * @param {Cart} cart
   * @returns {Promise<number>}
   */
  async #calculatePrice(cart) {
    const protection = await this.#getProtection(cart);
    if (!protection) return 0;

    // Navidium's own widget uses the market price when the product has one, otherwise the
    // store price converted at the current rate. Never both.
    // @ts-ignore - getNavidiumMarketPrice is a global from Navidium
    const marketPrice = typeof getNavidiumMarketPrice === 'function' ? await getNavidiumMarketPrice(protection.variant_id) : null;
    if (marketPrice) return Number(marketPrice);

    const rate = parseFloat(window.Shopify?.currency?.rate ?? '1') || 1;
    return Math.round(protection.price * 100 * rate);
  }

  /**
   * Asks Navidium which protection variant applies to the cart.
   *
   * @param {Cart} cart
   * @returns {Promise<ProtectionResult | null>}
   */
  async #getProtection(cart) {
    const config = await this.#waitForNavidium();
    if (!config || !this.#offeredForVisitor(config)) return null;

    const totalWithoutProtection = cart.items
      .filter(isProtectionItem)
      .reduce((total, item) => total - item.final_line_price, cart.total_price);

    if (totalWithoutProtection <= 0) return null;

    // @ts-ignore - nvdCalculateProtection is a global from Navidium
    const protection = await nvdCalculateProtection(totalWithoutProtection / 100, config);
    return protection?.variant_id ? protection : null;
  }

  /**
   * Respects the Navidium settings its own widget checks: the app's success flag, show on
   * cart, and international protection being turned off.
   *
   * @param {Record<string, any>} config
   * @returns {boolean}
   */
  #offeredForVisitor(config) {
    if (config.success === false || String(config.show_on_cart) === '0') return false;

    if (String(config.nvd_international) === '0') {
      const nations = (config.nvd_nations ?? []).map((/** @type {{name: string}} */ nation) => nation?.name);
      const visitorCountry = sessionStorage.getItem('nvdVisitorCountry');
      if (visitorCountry && visitorCountry !== 'undefined' && !nations.includes(visitorCountry)) return false;
    }

    return true;
  }

  /**
   * Waits for Navidium's script and cached config, which load asynchronously.
   *
   * @returns {Promise<Record<string, any> | null>}
   */
  async #waitForNavidium() {
    const start = Date.now();

    while (Date.now() - start < NAVIDIUM_WAIT_MS) {
      const config = sessionStorage.getItem('nvdconfig');
      // @ts-ignore - nvdCalculateProtection is a global from Navidium
      if (typeof nvdCalculateProtection === 'function' && config) {
        try {
          return JSON.parse(config);
        } catch {
          return null;
        }
      }
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    return null;
  }

  /** Writes the current state to the DOM. */
  #render() {
    const { price, checkoutLabel, checkoutTotal } = this.refs;
    const cartTotal = this.#cartTotal;
    const offered = this.#state === 'ready';

    this.dataset.protectionState = this.#state;
    this.toggleAttribute('data-no-price', !offered);

    if (price) price.textContent = offered ? this.#formatMoney(this.#priceCents) : '';

    const label = offered || this.#state === 'loading' ? this.dataset.labelProtected : this.dataset.labelStandard;
    if (label && checkoutLabel) checkoutLabel.textContent = label;

    if (checkoutTotal) {
      const total = offered && !this.#protectionInCart ? cartTotal + this.#priceCents : cartTotal;
      checkoutTotal.textContent = this.#formatMoney(total);
    }
  }

  /**
   * Formats cents like the Liquid money filter, using the shop money format and the cart currency.
   *
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const template = this.refs.moneyFormat?.content.textContent?.trim() || '${{amount}}';
    const currency = (this.dataset.currency || '').toUpperCase();
    const defaultPrecision = ZERO_DECIMAL_CURRENCIES.includes(currency) ? 0 : 2;

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      switch (placeholder) {
        case 'amount_no_decimals':
          return formatCents(cents, ',', '.', 0);
        case 'amount_with_comma_separator':
          return formatCents(cents, '.', ',', defaultPrecision);
        case 'amount_no_decimals_with_comma_separator':
          return formatCents(cents, '.', ',', 0);
        case 'amount_no_decimals_with_space_separator':
          return formatCents(cents, ' ', ',', 0);
        case 'amount_with_space_separator':
          return formatCents(cents, ' ', ',', defaultPrecision);
        case 'amount_with_period_and_space_separator':
          return formatCents(cents, ' ', '.', defaultPrecision);
        case 'amount_with_apostrophe_separator':
          return formatCents(cents, "'", '.', defaultPrecision);
        case 'currency':
          return currency;
        default:
          return formatCents(cents, ',', '.', defaultPrecision);
      }
    });
  }

  /**
   * Adds the protection line to the cart.
   *
   * @returns {Promise<'added' | 'not-applicable' | 'failed'>} "not-applicable" when Navidium
   * no longer offers protection for this cart, for example after a quantity change.
   */
  async addProtection() {
    const cart = await this.#fetchCart();
    if (cart.items.some(isProtectionItem)) return 'added';

    const protection = await this.#getProtection(cart);
    if (!protection) return 'not-applicable';

    const body = JSON.stringify({ items: [{ id: protection.variant_id, quantity: 1 }] });
    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const data = await response.json();

    return response.ok && !data.status ? 'added' : 'failed';
  }

  /**
   * Removes any protection line from the cart.
   *
   * @returns {Promise<boolean>} Whether the cart is free of protection afterwards.
   */
  async removeProtection() {
    const cart = await this.#fetchCart();
    const index = cart.items.findIndex(isProtectionItem);
    if (index === -1) return true;

    const body = JSON.stringify({ line: index + 1, quantity: 0 });
    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const data = await response.json();

    return response.ok && !data.errors;
  }

  /**
   * "Protected checkout": adds protection, then goes to checkout. If protection can't be
   * added the shopper is told and chooses to retry or continue without it.
   *
   * @param {Event} event
   */
  async checkoutWithProtection(event) {
    event.preventDefault();
    if (this.#checkoutInProgress) return;

    this.#setBusy(true);
    this.#hideError();

    // A click before the fee has loaded waits for it rather than skipping protection.
    if (this.#state === 'loading') await this.#pendingRefresh;

    if (this.#state !== 'ready') {
      this.#goToCheckout();
      return;
    }

    this.#pushDataLayer('checkout_with_protection');

    /** @type {'added' | 'not-applicable' | 'failed'} */
    let result = 'failed';
    try {
      result = await this.addProtection();
    } catch (error) {
      console.error('Package protection: could not add protection', error);
    }

    if (result !== 'failed') {
      this.#goToCheckout();
      return;
    }

    this.#setBusy(false);
    this.#showError();
  }

  /**
   * "Checkout without package protection": removes protection if present, then goes to checkout.
   *
   * @param {Event} event
   */
  async checkoutWithoutProtection(event) {
    event.preventDefault();
    if (this.#checkoutInProgress) return;

    this.#pushDataLayer('checkout_without_protection');
    await this.#continueWithoutProtection();
  }

  /** @param {Event} event */
  retryProtectedCheckout(event) {
    this.checkoutWithProtection(event);
  }

  /** @param {Event} event */
  async continueWithoutProtection(event) {
    event.preventDefault();
    if (this.#checkoutInProgress) return;

    this.#pushDataLayer('checkout_without_protection');
    await this.#continueWithoutProtection();
  }

  async #continueWithoutProtection() {
    this.#setBusy(true);
    this.#hideError();

    try {
      await this.removeProtection();
    } catch (error) {
      console.error('Package protection: could not remove protection', error);
    }

    this.#goToCheckout();
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#checkoutInProgress = busy;
    const { checkoutButton } = this.refs;
    if (!checkoutButton) return;

    // aria-disabled rather than disabled, so keyboard focus stays on the button.
    if (busy) {
      checkoutButton.setAttribute('aria-busy', 'true');
      checkoutButton.setAttribute('aria-disabled', 'true');
    } else {
      checkoutButton.removeAttribute('aria-busy');
      checkoutButton.removeAttribute('aria-disabled');
    }
  }

  #showError() {
    const { error, errorMessage } = this.refs;
    if (!error || !errorMessage) return;

    error.hidden = false;
    // Set the text after revealing the alert so screen readers announce it.
    requestAnimationFrame(() => {
      errorMessage.textContent = this.dataset.errorMessage ?? '';
    });
  }

  #hideError() {
    const { error, errorMessage } = this.refs;
    if (error) error.hidden = true;
    if (errorMessage) errorMessage.textContent = '';
  }

  /** @param {string} event */
  #pushDataLayer(event) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event });
  }

  #goToCheckout() {
    this.#checkoutInProgress = true;
    window.location.assign('/checkout');
  }
}

/**
 * @param {number} cents
 * @param {string} thousandsSeparator
 * @param {string} decimalSeparator
 * @param {number} precision
 * @returns {string}
 */
function formatCents(cents, thousandsSeparator, decimalSeparator, precision) {
  const [whole = '0', fraction = ''] = (cents / 100).toFixed(precision).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
  return precision > 0 ? `${grouped}${decimalSeparator}${fraction}` : grouped;
}

if (!customElements.get('cart-package-protection-component')) {
  customElements.define('cart-package-protection-component', CartPackageProtectionComponent);
}
